// src/utils/actionStore.js
// ─────────────────────────────────────────────────────────────────────────────
// ACTION STORE — Event-sourced workflow DAG engine
//
// PHILOSOPHY:
//   Don't save data. Save intent.
//   Every user action is a tiny JSON event: { type, params, meta }
//   That event can be replayed on any dataset later.
//
// HOW MODULES PLUG IN:
//   1. Fire a window CustomEvent:
//      window.dispatchEvent(new CustomEvent('dd:action', { detail: {
//        module:  'auto_eda',        // which module
//        type:    'clean:drop_null_cols',  // action type
//        label:   'Drop high-null columns (>50%)',  // human label
//        params:  { threshold: 0.5 },  // replay params (no data!)
//        meta:    { removed: 3 },      // result info (display only)
//      }}));
//
//   2. The store catches it, appends to the current session DAG,
//      updates RightPanel in real-time.
//
// MODULE REGISTRY:
//   Add a new module by adding ONE entry to MODULE_REGISTRY below.
//   Nothing else changes. The panel, DAG engine, replay all work automatically.
//
// DATA FLOW:
//   dd:action event
//     → appendAction()
//     → localStorage session DAG
//     → dd:dag:updated event
//       → WorkflowPanel re-renders
//
// STORAGE:
//   Session DAG:    localStorage  dd_dag_session_{userKey}
//   Saved pipelines: localStorage  dd_pipelines_{userKey}
//   Backend sync:   POST /pipelines  (named saves only, no data)
// ─────────────────────────────────────────────────────────────────────────────

// ── Module registry ───────────────────────────────────────────────────────────
// Each module declares: id, label, color, icon, and its action types.
// Adding a module here is the ONLY change needed to support it everywhere.
export const MODULE_REGISTRY = {
  auto_eda: {
    id:    'auto_eda',
    label: 'Auto EDA',
    color: '#22d3ee',
    icon:  '🔬',
    actionGroups: {
      data:  { label: 'Data',    icon: '📁' },
      clean: { label: 'Cleaning', icon: '🧹' },
      explore: { label: 'Explore', icon: '📊' },
      report:  { label: 'Report',  icon: '📝' },
    },
  },
  ml_experiments: {
    id:    'ml_experiments',
    label: 'ML Experiments',
    color: '#f97316',
    icon:  '⚙️',
    actionGroups: {
      data:  { label: 'Data',     icon: '📁' },
      model: { label: 'Model',    icon: '🤖' },
      train: { label: 'Training', icon: '🏋️' },
      eval:  { label: 'Evaluation', icon: '📈' },
    },
  },
  dl_experiments: {
    id:    'dl_experiments',
    label: 'DL Experiments',
    color: '#4ade80',
    icon:  '🧠',
    actionGroups: {
      data:    { label: 'Data', icon: '📁' },
      feature: { label: 'Features', icon: '🧩' },
      model:   { label: 'Architecture', icon: '🧠' },
      train:   { label: 'Training', icon: '🏋️' },
      eval:    { label: 'Evaluation', icon: '📈' },
      report:  { label: 'Report', icon: '📝' },
    },
  },
  doc_analysis: {
    id:    'doc_analysis',
    label: 'Doc Analysis',
    color: '#a78bfa',
    icon:  '📄',
    actionGroups: {
      data: { label: 'Data',    icon: '📁' },
      nlp:  { label: 'NLP',    icon: '🔤' },
      report: { label: 'Report', icon: '📝' },
    },
  },
  image_insights: {
    id:    'image_insights',
    label: 'Image Insights',
    color: '#fbbf24',
    icon:  '🖼️',
    actionGroups: {
      data:   { label: 'Data',    icon: '📁' },
      vision: { label: 'Vision', icon: '👁️' },
      report: { label: 'Report', icon: '📝' },
    },
  },
  audio_video: {
    id:    'audio_video',
    label: 'Audio & Video Studio',
    color: '#fbbf24',
    icon:  '🎙️',
    actionGroups: {
      speech:  { label: 'Speech',  icon: '🎙️' },
      video:   { label: 'Video',   icon: '🎬' },
      analyze: { label: 'Analyze', icon: '📊' },
      report:  { label: 'Report',  icon: '📝' },
    },
  },
  local_agents: {
    id:    'local_agents',
    label: 'Local Agents',
    color: '#34d399',
    icon:  '🤖',
    actionGroups: {
      config: { label: 'Configure', icon: '⚙️' },
      run:    { label: 'Run',       icon: '▶' },
    },
  },
};

// ── Known action type definitions ─────────────────────────────────────────────
// type → { label, group, icon, replayable, params_schema }
// replayable: true = can be included in saved pipeline replay
// params_schema: which params are needed for replay (validation hint)
export const ACTION_TYPES = {
  // Auto EDA
  'auto_eda:upload':           { label: 'Upload dataset',           group: 'data',   icon: '📤', replayable: false },
  'auto_eda:load_url':         { label: 'Load dataset URL',          group: 'data',   icon: '🔗', replayable: true, params_schema: ['url'] },
  'auto_eda:tab_switch':       { label: 'View tab',                  group: 'explore', icon: '🗂', replayable: false },
  // Cleaning ops (all replayable)
  'clean:drop_null_rows':      { label: 'Drop null rows',            group: 'clean',  icon: '🗑', replayable: true },
  'clean:drop_null_cols':      { label: 'Drop null columns (>50%)',   group: 'clean',  icon: '🗑', replayable: true, params_schema: ['threshold'] },
  'clean:fill_nulls':          { label: 'Fill nulls',                group: 'clean',  icon: '🔧', replayable: true, params_schema: ['col', 'method'] },
  'clean:remove_duplicates':   { label: 'Remove duplicates',         group: 'clean',  icon: '♻️', replayable: true },
  'clean:drop_column':         { label: 'Drop column',               group: 'clean',  icon: '✂️', replayable: true, params_schema: ['col'] },
  'clean:rename_column':       { label: 'Rename column',             group: 'clean',  icon: '✏️', replayable: true, params_schema: ['from', 'to'] },
  'clean:add_column':          { label: 'Add computed column',       group: 'clean',  icon: '➕', replayable: true, params_schema: ['name', 'expr'] },
  'clean:cast_column':         { label: 'Cast column type',          group: 'clean',  icon: '🔄', replayable: true, params_schema: ['col', 'type'] },
  'clean:filter_rows':         { label: 'Filter rows',               group: 'clean',  icon: '🔍', replayable: true, params_schema: ['col', 'op', 'val'] },
  'clean:remove_outliers':     { label: 'Remove outliers',           group: 'clean',  icon: '📉', replayable: true, params_schema: ['col', 'multiplier'] },
  'clean:normalize':           { label: 'Normalize column',          group: 'clean',  icon: '📊', replayable: true, params_schema: ['col', 'method'] },
  'clean:sort_rows':           { label: 'Sort rows',                 group: 'clean',  icon: '⬆️', replayable: true, params_schema: ['col', 'dir'] },
  'clean:search_replace':      { label: 'Search & replace',          group: 'clean',  icon: '🔄', replayable: true, params_schema: ['col', 'find', 'replace'] },
  'clean:undo':                { label: 'Undo',                      group: 'clean',  icon: '↩', replayable: false },
  'clean:redo':                { label: 'Redo',                      group: 'clean',  icon: '↪', replayable: false },
  // ML
  'ml:dataset_loaded':         { label: 'Dataset loaded',            group: 'data',   icon: '📁', replayable: false },
  'ml:target_selected':        { label: 'Target column set',         group: 'model',  icon: '🎯', replayable: true, params_schema: ['col'] },
  'ml:algorithm_selected':     { label: 'Algorithm selected',        group: 'model',  icon: '🤖', replayable: true, params_schema: ['algorithm'] },
  'ml:train_started':          { label: 'Model training started',    group: 'train',  icon: '🏋️', replayable: false },
  'ml:train_completed':        { label: 'Model training complete',   group: 'train',  icon: '✅', replayable: false },
  'ml:eval_viewed':            { label: 'Evaluation reviewed',       group: 'eval',   icon: '📈', replayable: false },
  // Doc analysis
  'doc:upload':                { label: 'Document uploaded',         group: 'data',   icon: '📤', replayable: false },
  'doc:nlp_run':               { label: 'NLP analysis run',          group: 'nlp',    icon: '🔤', replayable: false },
  'doc:deep_nlp_run':          { label: 'Deep NLP run',              group: 'nlp',    icon: '🧠', replayable: false },
  'doc:report_generated':      { label: 'Report generated',          group: 'report', icon: '📝', replayable: false },
  // Image
  'image:upload':              { label: 'Images uploaded',           group: 'data',   icon: '📤', replayable: false },
  'image:vision_run':          { label: 'Vision analysis run',       group: 'vision', icon: '👁️', replayable: false },
  'image:ocr_run':             { label: 'OCR run',                   group: 'vision', icon: '🔍', replayable: false },
  // Agent
  'agent:configured':          { label: 'Agent configured',          group: 'config', icon: '⚙️', replayable: false },
  'agent:brain_assigned':      { label: 'Brain model assigned',      group: 'config', icon: '🧠', replayable: false },
  'agent:launched':            { label: 'Agent launched',            group: 'run',    icon: '▶',  replayable: false },
};

// ── Canonical user key — use EVERYWHERE that touches DAG storage ─────────────
// Must match the priority used when calling initActionStore in App.jsx.
export function getUserKey(authUser) {
  if (!authUser) return 'anon';
  return authUser.username || authUser.email || authUser.id || authUser.user_id || authUser.uid || 'anon';
}

// ── Storage keys ──────────────────────────────────────────────────────────────
const sessionKey  = (uk) => `dd_dag_session_${uk}`;
const pipelinesKey = (uk) => `dd_pipelines_${uk}`;

// ── Helpers ───────────────────────────────────────────────────────────────────
function makeId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function emit(name, detail) {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch (_) {}
}

// ── Session DAG ───────────────────────────────────────────────────────────────
// The current-session action log. Stored as a flat array of nodes.
// Each node = one user action.
// The DAG structure is derived at render time from module + sequence.

export function loadSession(userKey) {
  try {
    const raw = localStorage.getItem(sessionKey(userKey));
    return raw ? JSON.parse(raw) : { nodes: [], startedAt: Date.now() };
  } catch (_) { return { nodes: [], startedAt: Date.now() }; }
}

export function saveSession(userKey, session) {
  try {
    // Cap at 500 nodes to avoid localStorage overflow
    const trimmed = { ...session, nodes: session.nodes.slice(-500) };
    localStorage.setItem(sessionKey(userKey), JSON.stringify(trimmed));
  } catch (_) {}
}

export function clearSession(userKey) {
  try { localStorage.removeItem(sessionKey(userKey)); } catch (_) {}
  emit('dd:dag:updated', { cleared: true });
}

// ── Append an action to the session ─────────────────────────────────────────
export function appendAction(userKey, action) {
  const session = loadSession(userKey);
  const def = ACTION_TYPES[action.type] || {};

  const node = {
    id:       makeId(),
    type:     action.type,
    module:   action.module || action.type.split(':')[0],
    label:    action.label || def.label || action.type,
    group:    action.group || def.group || 'misc',
    icon:     action.icon  || def.icon  || '◈',
    params:   action.params || {},   // replay params
    meta:     action.meta   || {},   // display-only info
    ts:       action.ts     || Date.now(),
    replayable: action.replayable ?? def.replayable ?? false,
    status:   'ok',
  };

  session.nodes.push(node);
  saveSession(userKey, session);
  emit('dd:dag:updated', { node, userKey });
  return node;
}

// ── Build DAG tree from flat node list ────────────────────────────────────────
// Groups nodes by module, then by session boundaries (dataset uploads),
// returning a tree structure for rendering.
//
// Returns:
// [
//   { module, label, color, icon, runs: [
//        { id, datasetName, startedAt, nodes: [...] }
//   ]}
// ]
export function buildDAGTree(nodes) {
  const grouped = {};
  nodes.forEach(n => {
    const mod = n.module || 'unknown';
    if (!grouped[mod]) grouped[mod] = [];
    grouped[mod].push(n);
  });

  return Object.entries(grouped).map(([mod, modNodes]) => {
    const modInfo = MODULE_REGISTRY[mod] || { label: mod, color: '#475569', icon: '◈' };

    const runs = [];
    let current = null;
    modNodes.forEach(n => {
      const isDatasetLoad = n.type === 'auto_eda:upload' || n.type === 'auto_eda:load_url' || n.type === 'ml:dataset_loaded' || n.type === 'doc:upload';
      if (isDatasetLoad || !current) {
        current = {
          id: makeId(),
          datasetName: n.meta?.name || n.label || 'Dataset',
          startedAt: n.ts,
          nodes: [],
        };
        runs.push(current);
      }
      current.nodes.push(n);
    });
    return { module: mod, label: modInfo.label, color: modInfo.color, icon: modInfo.icon, runs };
  });
}

// ── Extract a replayable pipeline from a run ──────────────────────────────────
export function extractPipeline(run, name, desc, moduleId) {
  const steps = run.nodes
    .filter(n => n.replayable)
    .map(n => ({ type: n.type, label: n.label, params: n.params, module: n.module }));

  return {
    id: makeId(),
    name,
    desc,
    module: moduleId,
    steps,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

// ── Pipelines storage ────────────────────────────────────────────────────────
export function loadPipelines(userKey) {
  try {
    const raw = localStorage.getItem(pipelinesKey(userKey));
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}

export function savePipeline(userKey, pipeline) {
  const pipelines = loadPipelines(userKey);
  const existing = pipelines.find(p => p.id === pipeline.id);
  if (existing) {
    Object.assign(existing, pipeline, { updatedAt: Date.now() });
  } else {
    pipelines.unshift({ ...pipeline, updatedAt: Date.now(), createdAt: Date.now() });
  }
  try {
    localStorage.setItem(pipelinesKey(userKey), JSON.stringify(pipelines));
  } catch (_) {}
  emit('dd:dag:updated', { pipelineSaved: pipeline });
  return pipelines;
}

export function deletePipeline(userKey, pipelineId) {
  let pipelines = loadPipelines(userKey);
  pipelines = pipelines.filter(p => p.id !== pipelineId);
  try {
    localStorage.setItem(pipelinesKey(userKey), JSON.stringify(pipelines));
  } catch (_) {}
  emit('dd:dag:updated', { pipelineDeleted: pipelineId });
  return pipelines;
}

// ── Pipeline replay ───────────────────────────────────────────────────────────
// Fires the platform events that correspond to each pipeline step.
// Modules already listen on dd:auto_eda:load_csv etc — replay hooks in.
//
// Each action type maps to a replay handler.
// If a module doesn't handle a replay event, it silently skips.
export function replayPipeline(pipeline, targetData, onProgress, onDone) {
  const steps = pipeline.steps || [];
  let i = 0;

  // Registry of replay handlers per action type
  const REPLAY_HANDLERS = {
    'auto_eda:load_url': (step) => {
      if (step.params?.url) {
        window.dispatchEvent(new CustomEvent('dd:auto_eda:load_url', {
          detail: { url: step.params.url }
        }));
      }
    },
    'clean:drop_null_cols': () => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'dropNullCols' }
      }));
    },
    'clean:drop_null_rows': () => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'dropNullRows' }
      }));
    },
    'clean:fill_nulls': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'fillNull', params: step.params }
      }));
    },
    'clean:remove_duplicates': () => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'removeDuplicates' }
      }));
    },
    'clean:drop_column': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'dropColumn', params: step.params }
      }));
    },
    'clean:filter_rows': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'filterRows', params: step.params }
      }));
    },
    'clean:remove_outliers': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'removeOutliers', params: step.params }
      }));
    },
    'clean:normalize': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'normalize', params: step.params }
      }));
    },
    'clean:cast_column': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'changeDatatype', params: step.params }
      }));
    },
    'clean:sort_rows': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:clean', {
        detail: { op: 'sortRows', params: step.params }
      }));
    },
    'ml:target_selected': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:ml', {
        detail: { op: 'setTarget', params: step.params }
      }));
    },
    'ml:algorithm_selected': (step) => {
      window.dispatchEvent(new CustomEvent('dd:replay:ml', {
        detail: { op: 'setAlgorithm', params: step.params }
      }));
    },
  };

  async function runNext() {
    if (i >= steps.length) {
      onDone?.({ success: true, stepsRun: i });
      return;
    }
    const step = steps[i];
    onProgress?.({ step: i, total: steps.length, label: step.label });

    const handler = REPLAY_HANDLERS[step.type];
    if (handler) {
      try { handler(step); } catch (_) {}
    }

    i++;
    // Small delay between steps so UI can catch up
    setTimeout(runNext, 120);
  }

  runNext();
}

// ── Global action listener setup ─────────────────────────────────────────────
// Call once in App.jsx (or wherever ChatProvider is).
// Listens for dd:action events from any module and appends to session DAG.
export function initActionStore(userKey) {
  if (!userKey || typeof window === 'undefined') return () => {};

  const handler = (e) => {
    if (!e.detail?.type) return;
    appendAction(userKey, e.detail);
  };

  window.addEventListener('dd:action', handler);

  return () => window.removeEventListener('dd:action', handler);
}

// ── Convenience: fire an action event (use in modules) ───────────────────────
// Modules call this instead of directly firing CustomEvents.
// Works even before actionStore is initialised (events just aren't captured).
export function trackAction(detail) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('dd:action', { detail }));
  } catch (_) {}
}

// ── Format helpers ────────────────────────────────────────────────────────────
export function formatRelTime(ts) {
  const diff = Date.now() - ts;
  if (diff < 5000)   return 'just now';
  if (diff < 60000)  return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return new Date(ts).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
}

export function getModuleInfo(moduleId) {
  return MODULE_REGISTRY[moduleId] || { id: moduleId, label: moduleId, color: '#475569', icon: '◈' };
}
